import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Headphones
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { formatDuration, formatFileSize } from '../../utils/formatters';
import { useCurrentUser } from '../../hooks/useApi';
import { Navigate } from 'react-router-dom';
import { SimplePodcastPlayer } from '../../components/podcast';

export const PodcastDetailAdmin: React.FC = () => {
  const { marketId, podcastId } = useParams<{ marketId: string; podcastId: string }>();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);

  // Redirect if user doesn't have admin or customize permission
  if (!user?.permissions?.includes('market_compass.admin') && !user?.permissions?.includes('market_compass.customize')) {
    return <Navigate to="/" replace />;
  }

  // Fetch podcast details
  const { data: podcast, isLoading: loadingPodcast } = useQuery({
    queryKey: ['admin', 'podcast', podcastId],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/podcasts/all');
      return response.data.podcasts.find((p: any) => p.id === parseInt(podcastId!));
    }
  });

  // Fetch market details
  const { data: market } = useQuery({
    queryKey: ['admin', 'market', marketId],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/markets/all');
      const markets = response.data?.markets || [];
      return markets.find((m: any) => m.id === parseInt(marketId!));
    }
  });


  if (loadingPodcast) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading podcast details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!podcast) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Podcast not found.
        </Alert>
      </Container>
    );
  }


  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/admin/hub/marketcompass/markets/${marketId}`)}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {market?.name} - Podcast Details
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {podcast.title}
            </Typography>
          </Box>
        </Box>

        {/* Audio Player */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Headphones sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Listen to Podcast
              </Typography>
            </Box>
            <SimplePodcastPlayer 
              podcast={{ 
                ...podcast,
                id: podcast.id,
                marketId: parseInt(marketId!),
                reportMonth: podcast.report_month
              }} 
            />
          </CardContent>
        </Card>

        {/* Podcast Information */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Podcast Information
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Podcast ID
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {podcast.id}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={podcast.is_published ? 'Published' : 'Draft'}
                        color={podcast.is_published ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {formatDuration(podcast.duration_seconds)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Market
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {podcast.market_name} ({podcast.country_name})
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Title
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {podcast.title}
                    </Typography>
                  </Grid>
                  
                  {podcast.description && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {podcast.description}
                      </Typography>
                    </Grid>
                  )}
                  
                  {podcast.speaker && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Speaker
                      </Typography>
                      <Typography variant="body1">
                        {podcast.speaker}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  File Information
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" color="text.secondary">
                  File Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, wordBreak: 'break-all' }}>
                  {podcast.file_name}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary">
                  File Size
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatFileSize(podcast.file_size_bytes)}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary">
                  Content Type
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {podcast.file_content_type}
                </Typography>
                
                {podcast.azure_blob_name && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">
                      Azure Blob Name
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, wordBreak: 'break-all' }}>
                      {podcast.azure_blob_name}
                    </Typography>
                  </>
                )}
                
                {podcast.azure_container_name && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">
                      Azure Container
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {podcast.azure_container_name}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Timestamps
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(podcast.created_at).toLocaleString()}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary">
                  Updated At
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(podcast.updated_at).toLocaleString()}
                </Typography>
                
                {podcast.published_at && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">
                      Published At
                    </Typography>
                    <Typography variant="body1">
                      {new Date(podcast.published_at).toLocaleString()}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};